/**
 * Integrytas Group - User-Defined Column Engine (UDC)
 * use_udc_grid - runtime LIST hydration for user-defined fields.
 *
 * Companion to useUdcFields (which feeds the edit form). Given a list page's
 * entity + page + already-loaded rows, this returns the custom fields an admin
 * placed on THIS page for the grid and/or the filter bar, plus everything a list
 * needs to render them:
 *   - columns   : GridColumnDef[] to append to the page's PersonalizedGrid
 *   - facets    : multi-value filter facets (ENUM → its values; else distinct
 *                 values present in the data)
 *   - renderCell: formatted cell value for a custom column key
 *   - matchRow  : whether a row passes the current facet selections
 *
 * The row values come from each entity's `ext_attributes` jsonb. That column
 * exists only once an entity is DSE-enabled, so we fetch it LAZILY by pk and only
 * when custom grid/filter fields are actually placed — a page that adds this hook
 * stays completely inert (no extra query, no breakage) until then.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiDb } from '../lib/api_client';
import { asfWorkspaceCode } from '../lib/asf';
import type { GridColumnDef } from '../components/personalization';
import type { FacetOption } from '../components/common/FacetFilter';

export interface UdcGridField {
  key: string;
  label: string;
  data_type: 'STRING' | 'INT' | 'NUMBER' | 'BOOL' | 'DATE' | 'DATETIME' | 'ENUM';
  control: string;
  allowed_values: string[];
  display_order: number;
  in_grid: boolean;
  in_filter: boolean;
}

export interface UdcFacetDef { key: string; label: string; options: FacetOption[] }

export interface UseUdcGrid {
  columns: GridColumnDef[];
  facets: UdcFacetDef[];
  isUdcKey: (key: string) => boolean;
  renderCell: (row: Record<string, unknown>, key: string) => string;
  /** Ascending comparator for a custom column (the page's sort applies direction). */
  compare: (a: Record<string, unknown>, b: Record<string, unknown>, key: string) => number;
  matchRow: (row: Record<string, unknown>) => boolean;
  filterValues: Record<string, string[]>;
  setFilter: (key: string, values: string[]) => void;
  clear: () => void;
  hasActiveFilters: boolean;
  loading: boolean;
}

interface Opts {
  pkColumn: string;
  rows: Record<string, unknown>[];
  workspaceCode?: string | null;
  enabled?: boolean;
}

function fmt(value: unknown, dt: UdcGridField['data_type']): string {
  if (value === null || value === undefined || value === '') return '';
  if (dt === 'BOOL') return value === true || value === 'true' || value === 'Y' ? 'Yes' : 'No';
  return String(value);
}

export function useUdcGrid(entity: string | null | undefined, pageCode: string, opts: Opts): UseUdcGrid {
  const { pkColumn, rows } = opts;
  const enabled = opts.enabled ?? true;
  const [fields, setFields] = useState<UdcGridField[]>([]);
  const [ext, setExt] = useState<Record<string, Record<string, unknown>>>({});
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);

  // 1. Which custom fields did the admin place on this page's grid / filter bar?
  useEffect(() => {
    if (!entity || !enabled) { setFields([]); return; }
    let cancelled = false;
    void (async () => {
      const ws = opts.workspaceCode !== undefined ? (opts.workspaceCode ?? '') : (asfWorkspaceCode() || '');
      const { data } = await apiDb.rpc<UdcGridField[]>('udc_grid_fields',
        { p_entity: entity, p_workspace_code: ws, p_page_code: pageCode });
      if (!cancelled) setFields(Array.isArray(data) ? data : []);
    })();
    return () => { cancelled = true; };
  }, [entity, pageCode, enabled, opts.workspaceCode]);

  // 2. Lazily fetch ext_attributes for the loaded rows — only when fields exist.
  const pkList = useMemo(
    () => rows.map(r => r[pkColumn]).filter(v => v !== null && v !== undefined),
    [rows, pkColumn],
  );
  const pkSig = useMemo(() => pkList.join(','), [pkList]);

  useEffect(() => {
    if (!entity || fields.length === 0 || pkList.length === 0) { setExt({}); return; }
    let cancelled = false;
    setLoading(true);
    void (async () => {
      // ext_attributes lives only on DSE-enabled entities; degrade quietly otherwise.
      const { data, error } = await apiDb.from(entity).select(`${pkColumn}, ext_attributes`).in(pkColumn, pkList);
      if (cancelled) return;
      const map: Record<string, Record<string, unknown>> = {};
      if (!error && Array.isArray(data)) {
        for (const r of data as Record<string, unknown>[]) {
          map[String(r[pkColumn])] = (r.ext_attributes as Record<string, unknown>) ?? {};
        }
      }
      setExt(map);
      setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, pkColumn, pkSig, fields.length]);

  const fieldByKey = useMemo(() => {
    const m = new Map<string, UdcGridField>();
    for (const f of fields) m.set(f.key, f);
    return m;
  }, [fields]);

  const valueOf = useCallback((row: Record<string, unknown>, key: string): unknown => {
    const own = row.ext_attributes as Record<string, unknown> | undefined; // page may already carry it
    if (own && key in own) return own[key];
    return ext[String(row[pkColumn])]?.[key];
  }, [ext, pkColumn]);

  const columns: GridColumnDef[] = useMemo(
    () => fields.filter(f => f.in_grid).map(f => ({ key: f.key, header: f.label, defaultWidth: 160 })),
    [fields],
  );

  const facets: UdcFacetDef[] = useMemo(() => {
    return fields.filter(f => f.in_filter).map(f => {
      let options: FacetOption[];
      if (f.data_type === 'ENUM' && f.allowed_values.length > 0) {
        options = f.allowed_values.map(v => ({ value: v, label: v }));
      } else if (f.data_type === 'BOOL') {
        options = [{ value: 'Yes', label: 'Yes' }, { value: 'No', label: 'No' }];
      } else {
        const seen = new Set<string>();
        for (const r of rows) { const s = fmt(valueOf(r, f.key), f.data_type); if (s) seen.add(s); }
        options = [...seen].sort().map(v => ({ value: v, label: v }));
      }
      return { key: f.key, label: f.label, options };
    });
  }, [fields, rows, valueOf]);

  const isUdcKey = useCallback((key: string) => fieldByKey.has(key), [fieldByKey]);

  const renderCell = useCallback((row: Record<string, unknown>, key: string): string => {
    const f = fieldByKey.get(key);
    return f ? fmt(valueOf(row, key), f.data_type) : '';
  }, [fieldByKey, valueOf]);

  const compare = useCallback((a: Record<string, unknown>, b: Record<string, unknown>, key: string): number => {
    const f = fieldByKey.get(key);
    if (!f) return 0;
    const av = valueOf(a, key), bv = valueOf(b, key);
    const aEmpty = av === null || av === undefined || av === '';
    const bEmpty = bv === null || bv === undefined || bv === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;            // empties sort last (ascending)
    if (bEmpty) return -1;
    if (f.data_type === 'NUMBER' || f.data_type === 'INT') return Number(av) - Number(bv);
    if (f.data_type === 'BOOL') return fmt(av, f.data_type).localeCompare(fmt(bv, f.data_type));
    // DATE/DATETIME are ISO-ish strings; STRING/ENUM compare lexically too.
    return String(av).localeCompare(String(bv));
  }, [fieldByKey, valueOf]);

  const matchRow = useCallback((row: Record<string, unknown>): boolean => {
    for (const [key, sel] of Object.entries(filterValues)) {
      if (!sel || sel.length === 0) continue;
      const f = fieldByKey.get(key);
      if (!f) continue;
      if (!sel.includes(fmt(valueOf(row, key), f.data_type))) return false;
    }
    return true;
  }, [filterValues, fieldByKey, valueOf]);

  const setFilter = useCallback((key: string, values: string[]) => {
    setFilterValues(prev => ({ ...prev, [key]: values }));
  }, []);
  const clear = useCallback(() => setFilterValues({}), []);
  const hasActiveFilters = useMemo(
    () => Object.values(filterValues).some(v => v && v.length > 0),
    [filterValues],
  );

  // Memoised so the returned OBJECT is as stable as its members already are
  // (every one is useMemo/useCallback/setState). Returning a fresh literal made
  // the object change identity on every render, which forced call sites to list
  // individual members (matchRow, compare) in their dependency
  // arrays — precise, but easy to get incomplete: `isUdcKey` was used by the
  // sort comparators and listed nowhere, so a page could sort with a stale
  // predicate. With a stable object, `[…, udcGrid]` is both correct and cheap.
  return useMemo(
    () => ({ columns, facets, isUdcKey, renderCell, compare, matchRow, filterValues, setFilter, clear, hasActiveFilters, loading }),
    [columns, facets, isUdcKey, renderCell, compare, matchRow, filterValues, setFilter, clear, hasActiveFilters, loading],
  );
}
